/**
 * SendNoteModal
 * Lets a logged-in user privately send a note to someone by email.
 * Identity can be shown, revealed-on-open, or fully anonymous.
 */

import React, { useState, useEffect, useCallback } from 'react';
import './SendNoteModal.css';
import { sendNoteByEmail } from '../../utils/noteShareApi';
import { logger } from '../../utils/logger';

const MAX_RECIPIENTS = 3;
const MAX_COVER = 200;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const IDENTITY_OPTIONS = [
  {
    key: 'identified',
    title: 'Sign it with your name',
    desc: 'They’ll see it’s from you.',
  },
  {
    key: 'reveal',
    title: 'Stay a mystery until they open it',
    desc: 'Hidden in the email — revealed once the note unfolds.',
  },
  {
    key: 'anonymous',
    title: 'Send anonymously',
    desc: 'Your name is never shown.',
  },
];

export default function SendNoteModal({ memory, user, onClose }) {
  const [emails, setEmails] = useState(['']);
  const [coverMessage, setCoverMessage] = useState('');
  const [identity, setIdentity] = useState('identified');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [sent, setSent] = useState(false);

  const updateEmail = (index, value) => {
    setEmails((prev) => prev.map((e, i) => (i === index ? value : e)));
    setError(null);
  };

  const addEmail = () => {
    if (emails.length < MAX_RECIPIENTS) setEmails((prev) => [...prev, '']);
  };

  const removeEmail = (index) => {
    setEmails((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSend = async () => {
    const recipients = emails.map((e) => e.trim().toLowerCase()).filter(Boolean);

    if (recipients.length === 0) {
      setError('Add at least one email address.');
      return;
    }
    const bad = recipients.find((e) => !EMAIL_RE.test(e));
    if (bad) {
      setError(`"${bad}" doesn’t look like a valid email.`);
      return;
    }

    setSending(true);
    setError(null);
    try {
      const result = await sendNoteByEmail(user, memory.id, {
        recipients,
        coverMessage: coverMessage.trim() || null,
        anonymous: identity === 'anonymous',
        revealOnOpen: identity === 'reveal',
      });

      if (result.success) {
        setSent(true);
      } else {
        setError(result.error || 'Something went wrong. Please try again.');
      }
    } catch (err) {
      logger.error('Send note failed:', err);
      setError('Something went wrong. Please try again.');
    } finally {
      setSending(false);
    }
  };

  // Close on escape
  const handleEscape = useCallback(
    (e) => {
      if (e.key === 'Escape') onClose();
    },
    [onClose]
  );
  useEffect(() => {
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [handleEscape]);

  return (
    <div className="send-note-overlay" onClick={onClose}>
      <div className="send-note-modal" onClick={(e) => e.stopPropagation()}>
        <button className="send-note-close" onClick={onClose} aria-label="Close">×</button>

        {sent ? (
          <div className="send-note-success">
            <div className="send-note-envelope" aria-hidden="true">
              <div className="send-note-envelope-flap" />
              <div className="send-note-seal">✦</div>
            </div>
            <h2>It’s on its way.</h2>
            <p>
              {emails.filter(Boolean).length > 1
                ? 'They’ll each find a note waiting, just for them.'
                : 'They’ll find a note waiting, just for them.'}
            </p>
            <button className="send-note-btn primary" onClick={onClose}>Done</button>
          </div>
        ) : (
          <>
            <div className="send-note-header">
              <span className="send-note-mark">✦</span>
              <h2>Leave this note for someone</h2>
              <p>Only they will be able to open it.</p>
            </div>

            <div className="send-note-preview" style={{ '--note-color': memory.color || '#E6B3C2' }}>
              <span>“{(memory.text || '').slice(0, 120)}{(memory.text || '').length > 120 ? '…' : ''}”</span>
            </div>

            <div className="send-note-field">
              <label>Send to</label>
              {emails.map((email, i) => (
                <div className="send-note-email-row" key={i}>
                  <input
                    type="email"
                    inputMode="email"
                    autoComplete="email"
                    placeholder="their@email.com"
                    value={email}
                    onChange={(e) => updateEmail(i, e.target.value)}
                  />
                  {emails.length > 1 && (
                    <button className="send-note-remove" onClick={() => removeEmail(i)} aria-label="Remove">×</button>
                  )}
                </div>
              ))}
              {emails.length < MAX_RECIPIENTS && (
                <button className="send-note-add" onClick={addEmail} type="button">+ add another</button>
              )}
            </div>

            <div className="send-note-field">
              <label>Add a line, if you’d like <span className="send-note-optional">optional</span></label>
              <textarea
                rows={2}
                maxLength={MAX_COVER}
                placeholder="A few words before they open it…"
                value={coverMessage}
                onChange={(e) => setCoverMessage(e.target.value)}
              />
              <span className="send-note-count">{coverMessage.length}/{MAX_COVER}</span>
            </div>

            <div className="send-note-field">
              <label>How should it be signed?</label>
              <div className="send-note-identity">
                {IDENTITY_OPTIONS.map((opt) => (
                  <button
                    key={opt.key}
                    type="button"
                    className={`send-note-identity-option ${identity === opt.key ? 'selected' : ''}`}
                    onClick={() => setIdentity(opt.key)}
                  >
                    <span className="send-note-radio" aria-hidden="true" />
                    <span className="send-note-identity-text">
                      <strong>{opt.title}</strong>
                      <small>{opt.desc}</small>
                    </span>
                  </button>
                ))}
              </div>
            </div>

            {error && <div className="send-note-error">{error}</div>}

            <button className="send-note-btn primary" onClick={handleSend} disabled={sending}>
              {sending ? 'Sending…' : 'Leave it for them'}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
